import { useEffect, useState } from "react";
import { DynamicFormDefinition, FormSubmission, FormsApi, FormContainer as FormContainerChild, DynamicFormControl } from "../../common";
import { Oval } from 'react-loader-spinner'
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { WpAlert } from "../../common";
import SanitizedHtml from "../../common/components/helpers/sanitized-html";
import { Alert } from "@mui/material";
import { useTrackEvent, withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin, appInsights } from '../../AppInsights';

const Forms = (props: any) => {

    let clientId: string;
    let formDefinitionId: string;

    if (process.env.NODE_ENV === 'production') {
        clientId = (window as any).clientId;
        formDefinitionId = (window as any).formDefinitionId;
    } else {
        clientId = process.env.REACT_APP_CLIENT_ID ?? '';
        formDefinitionId = process.env.REACT_APP_FORM_DEFINITION_ID ?? '';
    }

    const [successOpen, setSuccessOpen] = useState(false);
    const [failureOpen, setFailureOpen] = useState(false);
    const [clearForm, setClearForm] = useState(false);

    const [failureMessage, setFailureMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [form, setForm] = useState<DynamicFormDefinition | null>(null);
    const [loadingFinished, setLoadingFinished] = useState(false);
    const [hideSubmit, setHideSubmit] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0); // Initialize a state for forcing refresh

    const { executeRecaptcha } = useGoogleReCaptcha();

    if (process.env.REACT_APP_API_URL == null) {
        throw new Error("Forms API URL not set");
    }


    const formsApi = new FormsApi(process.env.REACT_APP_API_URL, clientId, process.env.REACT_APP_API_KEY);


    useEffect(function effectFunction() {

        const fetchFormDefinitions = async (): Promise<void> => {
            setLoadingFinished(false);
            setFormLoading(true);

            const forms = await formsApi.fetchFormDefinitions().finally(() => {
                setFormLoading(false);
                setLoadingFinished(true);
            });
            await shouldHideSubmitButton(forms[0]);

            setForm(forms[0]);
        }

        const fetchFormDefinition = async (id: string): Promise<void> => {
            const formDefinitionKey = `formDefinition-${id}`;
            const serializedDefinition = localStorage.getItem(formDefinitionKey);
            let form = serializedDefinition == null ? null : JSON.parse(serializedDefinition) as DynamicFormDefinition;

            if (form != null) {
                shouldHideSubmitButton(form);

                // If the result does not contain an id, do not load the form
                if (form.id == null) {
                    form = null;
                } else {
                    setForm(form);

                    const revision = await formsApi.fetchFormDefinitionRevision(form.id);

                    if (form.revision !== revision) {
                        setForm(null);
                        localStorage.removeItem(formDefinitionKey);
                        setRefreshKey(prevKey => prevKey + 1); // Force a refresh of the component to property reload the form
                    }
                }
            }
            else {
                setFormLoading(true);
                setLoadingFinished(false);

                form = await formsApi.fetchFormDefinition(id).finally(() => {
                    setFormLoading(false);
                    setLoadingFinished(true);
                });

                if (form != null && form.id != null) {
                    localStorage.setItem(formDefinitionKey, JSON.stringify(form));
                    setForm(form);
                }
            }
        }

        if (formDefinitionId) {
            fetchFormDefinition(formDefinitionId);
        } else {
            fetchFormDefinitions();
        }
    }, [formDefinitionId, refreshKey]);

    const verifyRecaptcha = async (): Promise<string | null> => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return null;
        }

        return await executeRecaptcha('formSubmission');
    };

    // TODO: This will prevent navigation, but need a way to determine if form has been changed
    // useUnload(e => {
    //     e.preventDefault();
    //     e.returnValue = '';
    // });

    return (
        <div style={{ minHeight: 100 }}>
            <div style={{ position: "sticky", width: 80, marginLeft: "auto", marginRight: "auto", top: "calc(50%)", zIndex: 9999 }}>
                <Oval color="#3f51b5" secondaryColor="#9ea7da" height={80} width={80} visible={formLoading || loading} />
            </div>
            {
                failureOpen &&
                <div style={{ paddingTop: 10, paddingBottom: 10 }}>
                    <Alert severity="error" onClose={() => { setFailureOpen(false) }}>There was a problem submitting the form. Please try again later.</Alert>
                </div>
            }
            {
                (!successOpen && form != null) &&
                <div style={{ opacity: loading ? .5 : 1 }}>
                    <FormContainerChild form={form} hideSubmit={hideSubmit} formLoading={formLoading} loading={loading} onSave={saveForm} onCancel={cancel}></FormContainerChild>
                </div>
            }
            {
                successOpen && !form?.successMessage &&
                <div>
                    <span>Submission successful!</span>
                </div>
            }
            {
                form == null && loadingFinished &&
                <div>
                    <Alert severity="error">There was a problem loading the form. Please try again later.</Alert>
                </div>
            }
            {
                successOpen && form?.successMessage &&
                <div>
                    <SanitizedHtml html={form.successMessage.html}></SanitizedHtml>
                </div>
            }
            {
                successOpen && (!form?.successMessage || form?.successMessage.showReturnButton) &&
                <div><a href="#" onClick={() => setSuccessOpen(false)}>Reset form</a></div>
            }
            {/* <WpAlert message={failureMessage} severity="error" open={failureOpen} setOpen={setFailureOpen} /> */}
        </div>
    );

    async function saveForm(): Promise<void> {
        const formJson: any = {};
        let requiredEmpty = false;

        form?.controls.forEach(control => {
            const value = control.state;

            if (!control.skipSerialization && control.selectPropertyOptions != null) {
                (control.state as string[])?.forEach(selectedValue => {
                    const propertyOption = control.selectPropertyOptions?.find(x => x.label === selectedValue);
                    if (propertyOption != null) {
                        formJson[propertyOption.jsonName] = true;
                    }
                });
            } else if (!control.skipSerialization && (control.jsonName != null || control.label != null)) {
                const propertyName = control.jsonName == null ? camelCase(control.label || '') : control.jsonName;

                // If the form option to not include empty fields is set, then ensure the form field has a value
                if (!form.outputOptions?.includeOnlyNonEmptyFields || value != null) {
                    formJson[propertyName] = value == null ? null : value;
                }
                if (control.toggleDisabled) {
                    const disabledPropertyName = control.disableToggleField == null ? propertyName + 'Disabled' : control.disableToggleField;
                    formJson[disabledPropertyName] = control.disabled;
                }
            }

            if (control.required && (control.state == null || control.state === '')) {
                requiredEmpty = true;
            }
        });

        if (requiredEmpty) {
            appInsights.trackEvent({ name: "Invalid Submission", properties: { form: form } });
            alert("Unable to submit form due to missing fields. If this message persists, please refresh the form and try again.");
            return;
        }

        const formSubmissionId = await postFormSubmission(formJson);
        if (formSubmissionId == null) {
            return;
        }

        const fileUploadControls = form?.controls.filter(x => x.fileUploadOptions != null);

        if (fileUploadControls != null) {
            for (let i = 0; i < fileUploadControls.length; i++) {
                const control = fileUploadControls[i];
                if (control.fileUploadOptions?.files) {
                    await postFileUpload(formSubmissionId, control.fileUploadOptions?.files);
                }
            }
        }

        setSuccessOpen(true);
        scrollToTop();

        let doRedirect = true;

        if (form?.successMessage?.redirectUrl != null && form.successMessage?.redirectConditionalField != null) {
            const conditionalField = form?.controls.find(x => x.id === form.successMessage?.redirectConditionalField);

            if ((conditionalField?.state != null && conditionalField?.state !== form.successMessage?.redirectConditionalValue) || conditionalField?.state == null) {
                doRedirect = false;
            }
        }

        form?.controls.forEach(x => {
            if (x?.updater?.setValue != null) {
                x.updater.setValue('');
            }
        });

        if (form?.successMessage?.redirectUrl && doRedirect) {
            window.location.href = form?.successMessage?.redirectUrl;
        }

        if (form?.successMessage?.javascriptCallbackName != null) {
            const callbackMethodName = form?.successMessage?.javascriptCallbackName;
            if (window.hasOwnProperty(callbackMethodName)) {
                (window as any)[callbackMethodName](formJson, form);
            } else {
                console.warn(`Callback function ${form?.successMessage?.javascriptCallbackName} not found!`)
            }
        }
    }

    function scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function cancel(): void {
        setClearForm(!clearForm);
    }

    async function postFormSubmission(formSubmission: any): Promise<string | null> {
        const token = await verifyRecaptcha();
        const submission: FormSubmission = {
            id: null,
            clientId: clientId,
            formDefinitionId: form?.id ?? '',
            formSubmissionData: formSubmission,
            recaptchaToken: token ?? '',
            isViewed: false
        };

        let submissionId: string | null = null;

        setLoading(true);

        await formsApi.submitForm(submission)
            .then(formSubmissionId => {
                submissionId = formSubmissionId;
            })
            .catch(error => {
                console.log(error);
                setFailureMessage(error);
                setFailureOpen(true);
            })
            .finally(() => setLoading(false));

        return submissionId;
    }

    async function postFileUpload(formSubmissionId: string, files: File[]): Promise<void> {
        setLoading(true);
        const token = await verifyRecaptcha();
        await formsApi.uploadFiles(formSubmissionId, files, token ?? '')
            .catch(error => {
                console.log(error);
                setFailureMessage('Form submitted, but unable to upload attachments.');
                setFailureOpen(true);
            })
            .finally(() => setLoading(false));
    }



    function shouldHideSubmitButton(form: DynamicFormDefinition): void {
        const shouldHide = form.controls?.some(control => control.type === 'submit');
        setHideSubmit(shouldHide);
    }

    function camelCase(str: string) {
        return str.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word: string, index: number) {
            return index === 0 ? word.toLowerCase() : word.toUpperCase();
        }).replace(/\s+/g, '');
    }
}

export default withAITracking(reactPlugin, Forms);
